import React, { useState, useEffect } from 'react';
import { motivoLegivel } from '@/services/erroLegivel';
import type {
  RadarItem,
  CycleItem,
  SpecialWeekItem,
  DayOfWeekItem,
} from '@/services/predictiveCalendarService';
import { predictiveCalendarService } from '@/services/predictiveCalendarService';
import { RadarCard } from '@/components/PredictiveCalendar/RadarCard';
import { DraftBuilderDrawer } from '@/components/PredictiveCalendar/DraftBuilderDrawer';

export const PredictiveCalendar: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'radar' | 'meses' | 'ciclos' | 'semana'>('radar');
  const [selectedMes, setSelectedMes] = useState<number>(new Date().getMonth() + 1);
  const [search, setSearch] = useState<string>('');

  const [radarItems, setRadarItems] = useState<RadarItem[]>([]);
  const [cycles, setCycles] = useState<CycleItem[]>([]);
  const [specialWeeks, setSpecialWeeks] = useState<SpecialWeekItem[]>([]);
  const [daysOfWeek, setDaysOfWeek] = useState<DayOfWeekItem[]>([]);

  const [loading, setLoading] = useState<boolean>(true);
  const [erro, setErro] = useState<string | null>(null);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [selectedItemForDraft, setSelectedItemForDraft] = useState<RadarItem | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  // A aba "mês a mês" renderizava specialWeeks INTEIRO: trocar o mês no seletor
  // não mudava uma linha. Filtrar aqui é o que faz o seletor significar algo.
  const semanasDoMes = specialWeeks.filter((w) => {
    const s = Number((w as { semana_num?: number }).semana_num);
    if (!Number.isFinite(s)) return true;
    const mesDaSemana =
      new Date(Date.UTC(new Date().getFullYear() + 1, 0, 1 + (s - 1) * 7)).getUTCMonth() + 1;
    return mesDaSemana === selectedMes;
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [radarRes, cyclesRes, weeksRes, daysRes] = await Promise.all([
        predictiveCalendarService.getRadar(selectedMes),
        predictiveCalendarService.getCycles(),
        predictiveCalendarService.getSpecialWeeks(),
        predictiveCalendarService.getDayOfWeek(),
      ]);
      setRadarItems(radarRes.items || []);
      setCycles(cyclesRes.cycles || []);
      setSpecialWeeks(weeksRes.weeks || []);
      setDaysOfWeek(daysRes.days || []);
    } catch (err) {
      // Sem estado de erro visivel, a falha deixava o dado nulo e a tela
      // ficava em "carregando" para sempre — que foi como as telas do
      // Estrategista se apresentaram durante toda a auditoria.
      setErro(motivoLegivel(err));
      console.error('Erro ao carregar dados do calendário preditivo:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedMes]);

  // F-411: havia um useTranslation() AQUI DENTRO — hook chamado no onClick,
  // fora do ciclo de render. React lança "Invalid hook call" no primeiro
  // clique: o botao principal de cada card do Radar crashava a tela. O `t`
  // nem era usado (void t) — sobra de um codemod de i18n.
  const handleOpenDraft = (item: RadarItem) => {
    setSelectedItemForDraft(item);
    setIsDrawerOpen(true);
  };

  const handleSuccessDraft = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 4000);
  };

  const filteredRadarItems = radarItems.filter(
    (i) =>
      i.termo.toLowerCase().includes(search.toLowerCase()) ||
      i.categoria.toLowerCase().includes(search.toLowerCase()),
  );

  const mesesNomes = [
    'Janeiro',
    'Fevereiro',
    'Março',
    'Abril',
    'Maio',
    'Junho',
    'Julho',
    'Agosto',
    'Setembro',
    'Outubro',
    'Novembro',
    'Dezembro',
  ];

  return (
    <div
      style={{
        padding: '24px',
        maxWidth: '1400px' /* i18n: PredictiveCalendar */,
        margin: '0 auto',
        color: 'var(--text-1)',
      }}
    >
      {/* Toast Notification */}
      {toastMsg && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            backgroundColor: 'var(--sig-ok)',
            color: 'var(--text-1)',
            padding: '12px 20px',
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '13px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            zIndex: 1100,
          }}
        >
          ✅ {toastMsg}
        </div>
      )}

      {/* Header Executivo */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '16px',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h1 style={{ fontSize: '24px', fontWeight: 800, margin: 0, color: 'var(--text-1)' }}>
              Radar de Antecipação & Calendário Preditivo
            </h1>
            <span
              style={{
                backgroundColor: 'rgba(16, 185, 129, 0.15)',
                color: 'var(--sig-ok)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                padding: '4px 10px',
                borderRadius: '20px',
                fontSize: '11.5px',
                fontWeight: 700,
              }}
            >
              Base nacional de sazonalidade
            </span>
          </div>
          <p style={{ fontSize: '13px', color: 'var(--text-3)', margin: '6px 0 0 0' }}>
            Ocasiões calculadas por regra (feriados, computus, datas presas a dia da semana) com a
            antecedência de cada uma MEDIDA em oito anos de acesso diário real. Os mesmos para todas
            as redações — o valor está na antecedência, não em personalização por site.
          </p>
        </div>

        {/* Badge de Escopo Nacional */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              padding: '8px 14px',
              backgroundColor: 'var(--bg-2)',
              border: '1px solid var(--nexus-brand)',
              borderRadius: '6px',
              color: 'var(--nexus-brand-hover)',
              fontSize: '13px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span>🌎</span>
            <span>Escopo: Brasil (Nacional)</span>
          </div>

          <a
            href="/config/calendario"
            style={{
              padding: '8px 14px',
              backgroundColor: 'var(--border-1)',
              color: 'var(--text-1)',
              borderRadius: '6px',
              textDecoration: 'none',
              fontSize: '12.5px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            ⚙️ Configurar Motor
          </a>
        </div>
      </div>

      {/* Abas de Navegação */}
      <div
        style={{
          display: 'flex',
          borderBottom: '1px solid var(--border-1)',
          marginBottom: '24px',
          gap: '8px',
        }}
      >
        <button
          onClick={() => setActiveTab('radar')}
          style={{
            padding: '10px 18px',
            backgroundColor: activeTab === 'radar' ? 'var(--nexus-brand-active)' : 'transparent',
            color: activeTab === 'radar' ? 'var(--text-1)' : 'var(--text-3)',
            border: 'none',
            borderBottom:
              activeTab === 'radar'
                ? '2px solid var(--nexus-brand-hover)'
                : '2px solid transparent',
            borderRadius: '6px 6px 0 0',
            fontWeight: 700,
            fontSize: '13px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          🔥 Radar de Ignição (Próximos 60 Dias)
        </button>

        <button
          onClick={() => setActiveTab('meses')}
          style={{
            padding: '10px 18px',
            backgroundColor: activeTab === 'meses' ? 'var(--nexus-brand-active)' : 'transparent',
            color: activeTab === 'meses' ? 'var(--text-1)' : 'var(--text-3)',
            border: 'none',
            borderBottom:
              activeTab === 'meses'
                ? '2px solid var(--nexus-brand-hover)'
                : '2px solid transparent',
            borderRadius: '6px 6px 0 0',
            fontWeight: 700,
            fontSize: '13px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          📅 Calendário Mês a Mês
        </button>

        <button
          onClick={() => setActiveTab('ciclos')}
          style={{
            padding: '10px 18px',
            backgroundColor: activeTab === 'ciclos' ? 'var(--nexus-brand-active)' : 'transparent',
            color: activeTab === 'ciclos' ? 'var(--text-1)' : 'var(--text-3)',
            border: 'none',
            borderBottom:
              activeTab === 'ciclos'
                ? '2px solid var(--nexus-brand-hover)'
                : '2px solid transparent',
            borderRadius: '6px 6px 0 0',
            fontWeight: 700,
            fontSize: '13px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          🏆 Grandes Ciclos (1, 2 e 4 Anos)
        </button>

        <button
          onClick={() => setActiveTab('semana')}
          style={{
            padding: '10px 18px',
            backgroundColor: activeTab === 'semana' ? 'var(--nexus-brand-active)' : 'transparent',
            color: activeTab === 'semana' ? 'var(--text-1)' : 'var(--text-3)',
            border: 'none',
            borderBottom:
              activeTab === 'semana'
                ? '2px solid var(--nexus-brand-hover)'
                : '2px solid transparent',
            borderRadius: '6px 6px 0 0',
            fontWeight: 700,
            fontSize: '13px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          ⏱️ Rotina Semanal (7 Dias)
        </button>
      </div>

      {erro ? (
        <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--text-3)' }}>
          <p style={{ color: 'var(--text-1)', fontWeight: 600, marginBottom: '6px' }}>
            Não foi possível carregar o radar.
          </p>
          <p style={{ fontSize: '13px' }}>{erro}</p>
        </div>
      ) : loading ? (
        <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text-3)' }}>
          Carregando o radar de ocasiões...
        </div>
      ) : (
        <>
          {/* ABA 1: RADAR DE IGNIÇÃO */}
          {activeTab === 'radar' && (
            <div>
              {/* Barra de Busca e Mês */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '20px',
                  gap: '12px',
                  flexWrap: 'wrap',
                }}
              >
                <input
                  type="text"
                  placeholder="Filtrar pautas por palavra-chave ou categoria..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    flex: 1,
                    minWidth: '280px',
                    padding: '10px 14px',
                    backgroundColor: 'var(--bg-2)',
                    border: '1px solid var(--border-2)',
                    borderRadius: '6px',
                    color: 'var(--text-1)',
                    fontSize: '13px',
                  }}
                />

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '12px', color: 'var(--text-3)', fontWeight: 600 }}>
                    Mês Alvo:
                  </span>
                  <select
                    value={selectedMes}
                    onChange={(e) => setSelectedMes(Number(e.target.value))}
                    style={{
                      padding: '10px',
                      backgroundColor: 'var(--bg-2)',
                      border: '1px solid var(--border-2)',
                      borderRadius: '6px',
                      color: 'var(--text-1)',
                      fontSize: '13px',
                      fontWeight: 600,
                    }}
                  >
                    {mesesNomes.map((nome, idx) => (
                      <option key={idx + 1} value={idx + 1}>
                        {nome} (Mês {idx + 1})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {filteredRadarItems.length === 0 ? (
                <div
                  style={{
                    padding: '40px',
                    textAlign: 'center',
                    backgroundColor: 'var(--bg-2)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-1)',
                  }}
                >
                  <p style={{ color: 'var(--text-3)', fontSize: '14px', margin: 0 }}>
                    Nenhuma pauta encontrada para os filtros selecionados.
                  </p>
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: '16px',
                  }}
                >
                  {filteredRadarItems.map((item) => (
                    <RadarCard key={item.id} item={item} onGenerateDraft={handleOpenDraft} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ABA 2: CALENDÁRIO MÊS A MÊS */}
          {activeTab === 'meses' && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
                gap: '16px',
              }}
            >
              {semanasDoMes.map((w) => (
                <div
                  key={w.semana_num}
                  style={{
                    backgroundColor: 'var(--bg-2)',
                    border: '1px solid var(--border-1)',
                    borderRadius: '8px',
                    padding: '16px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '8px',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '11px',
                        color: 'var(--nexus-brand-hover)',
                        fontWeight: 700,
                      }}
                    >
                      SEMANA {w.semana_num} • {w.total_ocasioes} ocasião(ões)
                    </span>
                    {w.maior_pico != null && (
                      <span style={{ fontSize: '11px', color: 'var(--text-3)', fontWeight: 600 }}>
                        pico {Number(w.maior_pico).toFixed(1)}x
                      </span>
                    )}
                  </div>
                  <h3 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 8px 0' }}>
                    {w.nome_semana}
                  </h3>
                  {w.maior_ignicao_dias != null && (
                    <div
                      style={{
                        fontSize: '12px',
                        color: 'var(--text-2)',
                        borderTop: '1px solid var(--border-1)',
                        paddingTop: '8px',
                      }}
                    >
                      Comece a produzir <strong>{w.maior_ignicao_dias} dias antes</strong> — é
                      quando a busca começa a subir, medido nos anos observados.
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ABA 3: GRANDES CICLOS */}
          {activeTab === 'ciclos' && (
            <div
              style={{
                overflowX: 'auto',
                backgroundColor: 'var(--bg-2)',
                borderRadius: '8px',
                border: '1px solid var(--border-1)',
              }}
            >
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '12.5px',
                  textAlign: 'left',
                }}
              >
                <thead>
                  <tr
                    style={{
                      backgroundColor: 'var(--bg-0)',
                      borderBottom: '1px solid var(--border-1)',
                      color: 'var(--text-3)',
                    }}
                  >
                    <th style={{ padding: '12px 16px' }}>Termo / Evento</th>
                    <th style={{ padding: '12px 16px' }}>Horizonte</th>
                    <th style={{ padding: '12px 16px' }}>Lead Time Recomendado</th>
                    <th style={{ padding: '12px 16px' }}>Arquétipo</th>
                    <th style={{ padding: '12px 16px' }}>Categoria</th>
                  </tr>
                </thead>
                <tbody>
                  {cycles.map((c, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <td style={{ padding: '12px 16px', fontWeight: 700, color: 'var(--text-1)' }}>
                        {c.termo}
                      </td>
                      <td style={{ padding: '12px 16px', color: 'var(--nexus-brand-hover)' }}>
                        {c.horizonte_temporal}
                      </td>
                      <td style={{ padding: '12px 16px', color: 'var(--sig-ok)', fontWeight: 600 }}>
                        {c.lead_time_dias_recomendado} dias (
                        {Math.round(c.lead_time_dias_recomendado / 7)} semanas)
                      </td>
                      <td style={{ padding: '12px 16px', color: 'var(--text-3)' }}>
                        {c.pico_x_base?.toFixed(1)}x · {c.anos_medidos} anos
                      </td>
                      <td style={{ padding: '12px 16px', color: 'var(--text-2)' }}>
                        {c.categoria_contextual}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ABA 4: ROTINA SEMANAL */}
          {activeTab === 'semana' && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: '16px',
              }}
            >
              {daysOfWeek.map((d) => (
                <div
                  key={d.dia_da_semana}
                  style={{
                    backgroundColor: 'var(--bg-2)',
                    border: '1px solid var(--border-1)',
                    borderRadius: '8px',
                    padding: '16px',
                  }}
                >
                  <div
                    style={{
                      fontSize: '13px',
                      fontWeight: 800,
                      color: 'var(--nexus-brand-hover)',
                      marginBottom: '6px',
                    }}
                  >
                    {d.dia_da_semana}
                  </div>
                  <div
                    style={{
                      fontSize: '22px',
                      fontWeight: 700,
                      marginBottom: '4px',
                      color: d.variacao_pct >= 0 ? 'var(--sig-ok)' : 'var(--sig-crit)',
                    }}
                  >
                    {d.variacao_pct >= 0 ? '+' : ''}
                    {d.variacao_pct.toFixed(1)}%
                  </div>
                  <div style={{ fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.4 }}>
                    contra a média do próprio assunto, medido em oito anos de acesso diário.
                    Substituiu um "perfil psicológico" que era opinião digitada, não medição.
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Drawer de Criação */}
      <DraftBuilderDrawer
        item={selectedItemForDraft}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSuccess={handleSuccessDraft}
      />
    </div>
  );
};
